import { useId, type ReactNode } from 'react';
import { Button, InlineAlert, Modal } from '@/components/ui';
import styles from '@/styles/components/ManageFormModal/style.scss';

interface ManageFormModalProps {
  title: string;
  submitLabel: string;
  busy: boolean;
  /** Server error from the last save, shown at the top of the form. */
  error: string | null;
  /** Why the form can't be saved yet; disables Save and shows as its tooltip. */
  blockedReason?: string;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}

/** Add/edit dialog for the Manage pages. Enter submits; Escape or Cancel closes. */
export function ManageFormModal({ title, submitLabel, busy, error, blockedReason, onSubmit, onClose, children }: ManageFormModalProps) {
  const formId = useId();

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      width={500}
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="primary" size="lg" disabled={busy || !!blockedReason} title={blockedReason}>
            {busy ? 'Saving…' : submitLabel}
          </Button>
        </>
      }
    >
      {error && <InlineAlert message={error} />}
      <form
        id={formId}
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && !blockedReason) onSubmit();
        }}
      >
        {children}
      </form>
    </Modal>
  );
}

/** Two fields side by side (one column on narrow screens). */
export function FormRow({ children }: { children: ReactNode }) {
  return <div className={styles.row}>{children}</div>;
}
